/**
 * Admin Growth Dashboard at `/admin/growth`.
 *
 * Single page that surfaces the only 5 numbers that actually move the
 * conservative-Y1 plan forward: persona funnel, paid-registration
 * throughput, channel attribution (UTM-source split), trainer
 * time-to-first-booking, and lead-magnet → paid conversion.
 *
 * The playbook itself (the prose advice that drove this build) lives
 * one click away at `/admin/growth/playbook` — pinned at the top of
 * the page so you can find it again any time.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendUpIcon as TrendUp, UsersIcon as Users, MegaphoneIcon as Megaphone, ClockIcon as Clock, EnvelopeSimpleIcon as EnvelopeSimple, ShareIcon as Share, ArrowRightIcon as ArrowRight, BookOpenIcon as BookOpen } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const personasOrdered = [
  ["all_users", "All users"],
  ["breeders", "Breeders"],
  ["buyers", "Buyers"],
  ["dog_owners", "Dog owners"],
  ["service_providers", "Service providers"],
  ["storefront_sellers", "Storefront sellers"],
];

export default function AdminGrowth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/growth/overview")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load growth data"));
  }, []);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold flex items-center gap-1.5">
              <TrendUp size={12} weight="duotone" /> Admin · Growth
            </p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">Growth dashboard</h1>
            <p className="text-sm text-[#666] max-w-2xl mt-2">
              The 5 numbers that move the conservative-Y1 plan forward. Refreshes live; no caching.
            </p>
          </div>
          <Link
            to="/admin/growth/playbook"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-4 py-2 text-sm font-semibold transition-colors"
            data-testid="growth-open-playbook"
          >
            <BookOpen size={14} /> Open the growth playbook
          </Link>
          <Link
            to="/admin/feature-matrix"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#0E0E0E] text-ink hover:bg-cream px-4 py-2 text-sm font-semibold transition-colors"
            data-testid="growth-open-feature-matrix"
          >
            <BookOpen size={14} /> Feature matrix
          </Link>
        </div>

        {err ? (
          <p className="text-sm text-[#C1272D]">{err}</p>
        ) : !data ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : (
          <>
            {/* Persona funnel */}
            <Section title="Signups by persona" icon={Users} testid="growth-personas">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {personasOrdered.map(([k, label]) => {
                  const v = data.personas?.[k] || {};
                  return (
                    <div key={k} className="bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid={`growth-persona-${k}`}>
                      <p className="text-xs uppercase tracking-wider text-[#888]">{label}</p>
                      <p className="font-spectral text-3xl tracking-tight mt-1">{fmt(v.lifetime)}</p>
                      <p className="text-[11px] text-[#666] mt-1">
                        Today {fmt(v.today)} · 7d {fmt(v.last_7d)} · 30d {fmt(v.last_30d)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Paid registrations */}
            <Section title="Paid $25 dog registrations" icon={TrendUp} testid="growth-paid-regs">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[["today", "Today"], ["last_7d", "7 days"], ["last_30d", "30 days"], ["lifetime", "Lifetime"]].map(([k, label]) => (
                  <Stat key={k} label={label} value={fmt(data.paid_registrations?.[k])} testid={`growth-paid-${k}`} />
                ))}
              </div>
            </Section>

            {/* Channel attribution */}
            <Section title="Channel attribution (UTM source)" icon={Megaphone} testid="growth-channels">
              <div className="grid lg:grid-cols-2 gap-4">
                <ChannelTable title="Last 30 days" rows={data.channel_last_30d || []} testid="growth-channels-30d" />
                <ChannelTable title="Lifetime"     rows={data.channel_lifetime  || []} testid="growth-channels-lifetime" />
              </div>
              <p className="text-[11px] text-[#888] mt-2">
                Use UTM tags on every link (Meta, FB, IG, YouTube descriptions, partnership pages) so this table fills out. <Link to="/admin/growth/playbook#utm" className="underline">How to UTM-tag every channel →</Link>
              </p>
            </Section>

            {/* Time-to-first booking + lead magnet */}
            <Section title="Trainer activation & lead funnel" icon={Clock} testid="growth-activation">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat
                  label="Trainer median days to 1st booking"
                  value={data.trainer_median_days_to_first_booking == null ? "—" : `${data.trainer_median_days_to_first_booking}d`}
                  testid="growth-time-to-booking"
                />
                <Stat
                  label="Lead-magnet signups (30d)"
                  value={fmt(data.lead_magnet?.last_30d)}
                  testid="growth-leads-30d"
                />
                <Stat
                  label="Lead → paid (30d)"
                  value={fmt(data.lead_magnet?.converted_to_paid_30d)}
                  hint={
                    data.lead_magnet?.last_30d > 0
                      ? `${((100 * (data.lead_magnet.converted_to_paid_30d || 0)) / data.lead_magnet.last_30d).toFixed(1)}% conversion`
                      : "no leads yet"
                  }
                  testid="growth-lead-conv"
                />
                <Stat
                  label="Referral conversions (lifetime)"
                  value={fmt(data.referrals?.lifetime_conversions)}
                  hint={`${fmt(data.referrals?.lifetime_visits)} visits`}
                  testid="growth-refs"
                />
              </div>
            </Section>

            {/* Platform flags: US-only signups + founding promo */}
            <Section title="Go worldwide — platform flags" icon={Share} testid="growth-flags">
              <PlatformFlagsCard />
            </Section>

            <p className="text-[11px] text-[#888] mt-6">
              Generated {new Date(data.generated_at).toLocaleString()} · refresh the page for new numbers.
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}

/**
 * Admin controls for the worldwide-launch posture + founding promo:
 *   - Worldwide buyers (inverse of us_only signups)
 *   - US sellers only (Connect payouts/storefronts gate)
 *   - Pricing currency (locked USD)
 *   - Stripe Tax on all checkouts
 *   - International-waitlist launch announcement
 */
function PlatformFlagsCard() {
  const [policy, setPolicy] = useState(null);
  const [promo, setPromo] = useState({ enabled: false, code: "FOUNDING20", percent: 20 });
  const [busy, setBusy] = useState(false);
  const [announcing, setAnnouncing] = useState(false);

  const loadPolicy = () =>
    api.get("/admin/platform/region-policy").then((r) => setPolicy(r.data)).catch(() => {});

  useEffect(() => {
    loadPolicy();
    api.get("/public/founding-promo").then((r) => {
      if (r.data?.enabled) setPromo({ enabled: true, code: r.data.code, percent: r.data.percent });
    }).catch(() => {});
  }, []);

  const setFlag = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/platform/region-policy", patch);
      setPolicy(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update flag");
    } finally {
      setBusy(false);
    }
  };

  const announce = async () => {
    if (!window.confirm(
      `Email the launch announcement to ${policy.international_waitlist_unnotified} waitlisted ${policy.international_waitlist_unnotified === 1 ? "person" : "people"}? Already-notified entries are skipped.`
    )) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post("/admin/platform/waitlist-announce");
      toast.success(`Launch announcement sent to ${data.sent} ${data.sent === 1 ? "person" : "people"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      loadPolicy();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Announcement failed");
    } finally {
      setAnnouncing(false);
    }
  };

  const savePromo = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/admin/platform/founding-promo", {
        enabled,
        code: promo.code || "FOUNDING20",
        percent: Number(promo.percent) || 20,
      });
      setPromo((p) => ({ ...p, enabled }));
    } finally {
      setBusy(false);
    }
  };

  if (!policy) return <Skeleton className="h-28" />;

  const Flag = ({ label, hint, value, onToggle, testid, locked, lockedLabel }) => (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5 flex items-center justify-between gap-3" data-testid={testid}>
      <div>
        <p className="font-spectral text-base tracking-tight">{label}</p>
        <p className="text-[12px] text-[#666] mt-0.5 leading-relaxed">{hint}</p>
      </div>
      {locked ? (
        <span className="rounded-full px-4 py-1.5 text-xs font-bold bg-[#F2EFE6] text-[#8A6A00] flex-shrink-0" data-testid={`${testid}-locked`}>
          {lockedLabel}
        </span>
      ) : (
        <button
          onClick={onToggle}
          disabled={busy}
          aria-pressed={value}
          className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors flex-shrink-0 ${
            value ? "bg-ink text-gold" : "bg-[#EAEAEA] text-[#666]"
          }`}
          data-testid={`${testid}-toggle`}
        >
          {value ? "ON" : "OFF"}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <Flag
          label="Worldwide buyers"
          hint={`International visitors can register and buy. Currently ${policy.us_only ? "OFF — non-US signups are waitlisted" : "ON — anyone can sign up"}.`}
          value={!policy.us_only}
          onToggle={() => setFlag({ us_only: !policy.us_only })}
          testid="flag-worldwide-buyers"
        />
        <Flag
          label="US sellers only"
          hint="US sellers keep platform-routed payouts (destination charges). When OFF, sellers outside the US onboard their OWN Stripe account as merchant of record (direct charges) — they carry all refund/dispute/tax liability, buyers see a recorded opt-in disclosure, and escrow stays US-only. Manage them at /admin/intl-sellers."
          value={policy.sellers_us_only}
          onToggle={() => setFlag({ sellers_us_only: !policy.sellers_us_only })}
          testid="flag-us-sellers"
        />
        <Flag
          label="International listings (contact-to-buy)"
          hint="Non-US breeders can pay to list dogs/puppies. Their listings show an 'International — contact to buy' badge; Buy-now/escrow stays US-only, so the sale is settled directly between buyer and breeder. You still earn their registration, plan, and listing fees."
          value={policy.intl_listings}
          onToggle={() => setFlag({ intl_listings: !policy.intl_listings })}
          testid="flag-intl-listings"
        />
        <Flag
          label="Pricing currency"
          hint="All prices are charged in USD; international buyers' banks convert automatically. Multi-currency is a future expansion."
          locked
          lockedLabel="USD"
          testid="flag-currency"
        />
        <Flag
          label="Stripe Tax on all checkouts"
          hint="Adds automatic tax (VAT/GST/sales tax) to every platform checkout. Configure your origin address in Stripe Dashboard → Tax first — checkouts fall back to pre-tax gracefully if Stripe Tax isn't ready."
          value={policy.stripe_tax_all}
          onToggle={() => setFlag({ stripe_tax_all: !policy.stripe_tax_all })}
          testid="flag-stripe-tax"
        />
      </div>

      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="waitlist-announce-card">
        <div>
          <p className="font-spectral text-base tracking-tight">International waitlist</p>
          <p className="text-[12px] text-[#666] mt-0.5 leading-relaxed">
            {fmt(policy.international_waitlist_count)} signed up ·{" "}
            {fmt(policy.international_waitlist_unnotified)} not yet notified. Sends a
            one-time &ldquo;we&rsquo;ve launched in your country&rdquo; email with a register link.
          </p>
          {(policy.seller_payout_demand || []).length > 0 && (
            <p className="text-[12px] text-[#666] mt-1.5" data-testid="seller-payout-demand">
              <span className="font-semibold text-ink">Seller-payout demand:</span>{" "}
              {policy.seller_payout_demand.map((d) => `${d.country} (${d.count})`).join(" · ")}
            </p>
          )}
        </div>
        <button
          onClick={announce}
          disabled={announcing || !policy.international_waitlist_unnotified}
          className="rounded-full px-4 py-2 text-xs font-bold bg-ink text-gold disabled:opacity-40 flex-shrink-0"
          data-testid="waitlist-announce-btn"
        >
          {announcing ? "Sending…" : "Send launch announcement"}
        </button>
      </div>

      <div className="bg-white border border-[#EAEAEA] rounded-2xl p-5" data-testid="flag-founding-promo">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <p className="font-spectral text-base tracking-tight">Founding-member promo banner</p>
            <p className="text-[12px] text-[#666] mt-0.5 leading-relaxed">
              Shows a promo strip on the pricing pages + dashboard upgrade
              banner. Enabling auto-creates the promo code.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <input
                value={promo.code}
                onChange={(e) => setPromo((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                className="w-32 rounded-lg border border-[#E5E5E5] px-2.5 py-1.5 text-xs font-mono"
                data-testid="flag-promo-code-input"
              />
              <input
                type="number"
                min={1}
                max={90}
                value={promo.percent}
                onChange={(e) => setPromo((p) => ({ ...p, percent: e.target.value }))}
                className="w-16 rounded-lg border border-[#E5E5E5] px-2.5 py-1.5 text-xs"
                data-testid="flag-promo-percent-input"
              />
              <span className="text-xs text-[#666]">% off</span>
            </div>
          </div>
          <button
            onClick={() => savePromo(!promo.enabled)}
            disabled={busy}
            aria-pressed={promo.enabled}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors flex-shrink-0 ${
              promo.enabled ? "bg-ink text-gold" : "bg-[#EAEAEA] text-[#666]"
            }`}
            data-testid="flag-promo-toggle"
          >
            {promo.enabled ? "ON" : "OFF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: I, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.2em] text-[#888] font-bold flex items-center gap-1.5 mb-3">
        <I size={12} /> {title}
      </p>
      {children}
    </div>
  );
}

function Stat({ label, value, hint, testid }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-[#888]">{label}</p>
      <p className="font-spectral text-3xl tracking-tight mt-1">{value}</p>
      {hint && <p className="text-[11px] text-[#666] mt-1">{hint}</p>}
    </div>
  );
}

function ChannelTable({ title, rows, testid }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-2xl p-4" data-testid={testid}>
      <p className="font-spectral text-base tracking-tight mb-2">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#888]">No UTM-tagged signups yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.source} className="flex items-baseline justify-between text-sm">
              <span className="text-[#3F3F3F]">{r.source}</span>
              <span className="font-tabular font-semibold">{fmt(r.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
